<script setup lang="ts">
import { IonSelect } from '@ionic/vue';

export interface SelectOption {
  value: unknown;
  label: string;
}

withDefaults(
  defineProps<{
    modelValue: unknown;
    options?: SelectOption[];
    error?: boolean;
    flat?: boolean;
    fullWidth?: boolean;
    icon?: string;
  }>(),
  {
    options: () => [],
    flat: false,
    fullWidth: true,
  }
);

const emit = defineEmits(['update:modelValue']);
</script>

<template>
  <ion-select
    :value="modelValue"
    @ionChange="e => emit('update:modelValue', e.detail.value)"
    v-bind="$attrs"
    :class="['base-select', { 'ion-invalid': error, 'flat-select': flat, 'no-full-width': !fullWidth }]"
    class="base-select"
    label-placement="stacked"
    fill="outline"
    :toggle-icon="icon"
    interface="action-sheet"
    mode="md"
  >
    <ion-select-option v-for="option in options" :key="option.label" :value="option.value">
      {{ option.label }}
    </ion-select-option>
  </ion-select>
</template>

<style lang="scss">
@import '@/scss/setup/_variables.scss';
@import '@/scss/setup/_functions.scss';

.base-select {
  --background: var(--ion-color-dark);
  --border-radius: 0.5rem;
  --padding-start: 0.75rem;
  --padding-end: 0.75rem;
  --placeholder-color: var(--ion-color-medium-light);
  --placeholder-opacity: 1;

  color: var(--ion-color-tertiary);
  min-height: 2.75rem;
  font-size: 1rem;

  &::part(icon) {
    color: inherit;
    scale: 1.5;
  }

  /* Conditional Full Width */
  &.no-full-width {
    max-width: 6rem; // Optional: set a minimum width
  }
  /* Flat Style - No Border */
  &.flat-select {
    --border-width: 0;
    --box-shadow: none;
  }

  /* Border */
  &.has-value {
    --border-color: var(--ion-color-tertiary);
  }

  &:not(.has-value) {
    --border-color: var(--ion-color-light-medium);
  }

  &.select-expanded {
    --border-color: var(--ion-color-primary);
  }

  /* Disabled */
  &.select-disabled {
    opacity: 1;
    --border-color: var(--ion-color-medium-light);
    --placeholder-color: var(--ion-color-medium-light);
    color: var(--ion-color-medium-light);
  }

  /* Invalid */
  &.ion-invalid {
    --border-color: var(--ion-color-danger);
  }
}

.action-sheet-selected {
  position: relative;
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    right: 1rem;
    transform: translateY(-0.5rem);
    width: 1.5rem; /* Adjust size */
    height: 1.5rem; /* Adjust size */
    background-image: url('../../assets/svg/icons/check.svg'); /* Use the SVG */
    background-size: contain;
    background-repeat: no-repeat;
  }
  &::after {
    background: transparent !important;
  }
}
</style>
